/**
 * Queue Manager
 * Extracted from WebSocketManager.ts
 * Handles operation and message queue management
 */

import { QueuedOperation } from "@/shared/apiTypes";
import type { NodeOperation } from '@/shared/types/projectData';
import { resolveQueuedOperationIdentity } from "./queuedOperationIdentity";
import { log } from "@/shared/utils/logger";

export interface QueueManagerConfig {
  maxQueueSize: number;
  maxRetries: number;
  onSyncStateChange: (updates: Record<string, unknown>) => void;
}

export class QueueManager {
  private operationQueue: QueuedOperation[] = [];
  private messageQueue: Array<{ message: Record<string, unknown>; timestamp: number; id: string }> = [];
  private config: QueueManagerConfig;

  constructor(config: QueueManagerConfig) {
    this.config = config;
  }

  // Queue operation with size limit
  queueOperation(operation: QueuedOperation): void {
    if (this.operationQueue.length >= this.config.maxQueueSize) {
      throw new Error('Operation queue is full; operation was not queued');
    }
    if (this.operationQueue.some(pending => pending.id === operation.id && pending.projectId === operation.projectId)) {
      throw new Error('Operation identity is already pending');
    }

    this.operationQueue.push(operation);
    this.config.onSyncStateChange({
      pendingOperations: [...this.operationQueue],
      syncStatus: "pending",
    });
  }

  // Queue non-operation messages
  queueMessage(message: Record<string, unknown>): QueuedOperation | null {
    log.debug("WebSocket", "Queuing message for later delivery:", message.type);

    if (message.type === "operation") {
      if (!isNodeOperation(message.operation)) {
        log.warn('WebSocket', 'Dropping invalid queued operation message');
        return null;
      }
      const envelopeId = typeof message.operationId === 'string' && message.operationId ? message.operationId : undefined;
      const { operationId } = resolveQueuedOperationIdentity({ ...message.operation }, envelopeId || `op_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`);
      // Handle operation messages
      const queuedOp: QueuedOperation = {
        id: operationId,
        operation: message.operation,
        timestamp: typeof message.timestamp === 'number' ? message.timestamp : Date.now(),
        retries: 0,
        maxRetries: this.config.maxRetries,
        projectId:
          (typeof message.project_id === "string" && message.project_id) ||
          (typeof message.projectId === "string" && message.projectId) ||
          undefined,
        sessionId:
          (typeof message.session_id === "string" && message.session_id) ||
          (typeof message.sessionId === "string" && message.sessionId) ||
          undefined,
      };
      this.queueOperation(queuedOp);
      return queuedOp;
    } else {
      // Queue non-operation messages (subscribe-project, unsubscribe-project, etc.)
      // Add queue size limit to prevent memory exhaustion
      if (this.messageQueue.length >= this.config.maxQueueSize) {
        // Remove oldest 20% of messages (keep recent ones)
        const removeCount = Math.max(1, Math.floor(this.config.maxQueueSize * 0.2));
        this.messageQueue = this.messageQueue.slice(removeCount);
        log.warn(
          "WebSocket",
          `Message queue size exceeded (${this.config.maxQueueSize}), pruned ${removeCount} oldest messages`
        );
      }

      this.messageQueue.push({
        message,
        timestamp: Date.now(),
        id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      });
      log.debug(
        "WebSocket",
        `Queued ${message.type} message. Queue size: ${this.messageQueue.length}`
      );
      return null;
    }
  }

  // Send all queued non-operation messages
  syncMessageQueue(sendFn: (message: Record<string, unknown>) => void): void {
    if (this.messageQueue.length === 0) return;

    log.info("WebSocket", `Sending ${this.messageQueue.length} queued messages`);
    // Keep failed work at the head and leave the unattempted tail in place.
    // A detached batch could be lost or exceed capacity under reentrant sends.
    const count = this.messageQueue.length;
    for (let i = 0; i < count; i++) {
      const entry = this.messageQueue[0];
      if (!entry) break;
      try {
        sendFn(entry.message);
      } catch {
        break;
      }
      this.messageQueue = this.messageQueue.filter(pending => pending !== entry);
    }
  }

  // Get all queued operations for sync
  getOperationsToSync(): QueuedOperation[] {
    const toSync = [...this.operationQueue];
    this.operationQueue = [];
    return toSync;
  }

  // Remove only the operation being transferred to ACK tracking. The rest stay
  // visible to admission checks while subscribers run during a flush.
  removeOperation(operation: QueuedOperation): void {
    this.operationQueue = this.operationQueue.filter(pending => pending !== operation);
  }

  // Re-queue failed operation
  requeueOperation(operation: QueuedOperation): void {
    this.operationQueue.push(operation);
  }

  // Update sync state after sync
  updateAfterSync(): void {
    this.config.onSyncStateChange({
      pendingOperations: [...this.operationQueue],
      syncStatus: this.operationQueue.length === 0 ? "synced" : "pending",
      lastSyncTime: Date.now(),
    });
  }

  getQueuedOperations(): QueuedOperation[] {
    return [...this.operationQueue];
  }

  // Get queue lengths
  getOperationQueueLength(): number {
    return this.operationQueue.length;
  }

  getMessageQueueLength(): number {
    return this.messageQueue.length;
  }

  // Clear all queues
  clearQueues(): void {
    this.operationQueue = [];
    this.messageQueue = [];
  }
}

export const isNodeOperation = (value: unknown): value is NodeOperation =>
  value !== null &&
  typeof value === 'object' && !Array.isArray(value) &&
  typeof Reflect.get(value, 'type') === 'string' &&
  NODE_OPERATION_TYPES.has(Reflect.get(value, 'type')) &&
  ['nodeId', 'parentId', 'columnId', 'sessionId', 'userId'].every(key =>
    Reflect.get(value, key) === undefined || typeof Reflect.get(value, key) === 'string') &&
  ['targetParentId', 'sourceParentId'].every(key =>
    Reflect.get(value, key) == null || typeof Reflect.get(value, key) === 'string') &&
  ['nodeIds', 'sourceNodeIds', 'columnOrder'].every(key => {
    const field = Reflect.get(value, key);
    return field === undefined || (Array.isArray(field) && field.every(item => typeof item === 'string'));
  }) &&
  ['data', 'metadata', 'projectData', 'columnData'].every(key => {
    const field = Reflect.get(value, key);
    return field === undefined || (field !== null && typeof field === 'object' && !Array.isArray(field));
  }) &&
  ['timestamp', 'targetIndex', 'daysDelta', 'progress'].every(key => {
    const field = Reflect.get(value, key);
    return field === undefined || (typeof field === 'number' && Number.isFinite(field));
  }) &&
  (Reflect.get(value, 'updates') === undefined || (
    Array.isArray(Reflect.get(value, 'updates')) && Reflect.get(value, 'updates').every((entry: unknown) =>
      entry !== null && typeof entry === 'object' && !Array.isArray(entry) &&
      typeof Reflect.get(entry, 'nodeId') === 'string' &&
      Reflect.get(entry, 'data') !== null && typeof Reflect.get(entry, 'data') === 'object' &&
      !Array.isArray(Reflect.get(entry, 'data'))
    )
  ));

const NODE_OPERATION_TYPES = new Set<string>([
  'create', 'update', 'delete', 'delete-tree', 'restore-tree', 'move', 'reorder', 'batch-delete', 'batch-update',
  'group-move', 'reorder-columns', 'add-column', 'update-column', 'delete-column',
  'progress-update', 'initial-load', 'copy-nodes', 'cut-nodes',
  'paste-nodes', 'duplicate-node', 'duplicate-subtree', 'attach-file', 'detach-file',
  'delete-file', 'rename-file',
]);
